import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { LegalLinks } from "../components/LegalLinks";
import { BrandText } from "../components/ULoveULogo";
import { BannerCarousel, useBanners } from "../components/BannerCarousel";
import { LangTextToggle } from "../components/common";
import { useLang } from "../lib/i18n";
import { useAppData } from "../context/AppDataContext";
import { useRegion } from "../context/RegionContext";
import { useAuth } from "../context/AuthContext";
import { useUnread } from "../context/UnreadContext";

function NotificationBell() {
  const { user } = useAuth();
  const { unread } = useUnread();
  const { t } = useLang();
  const navigate = useNavigate();
  if (!user) return null;
  const n = unread.total;
  return (
    <button
      onClick={() => navigate("/perfil", { state: { focus: "activity" } })}
      className="absolute left-3 top-safe-3 z-20 flex h-9 w-9 items-center justify-center rounded-full bg-white/75 shadow-sm backdrop-blur-md transition-transform duration-200 hover:scale-105 active:scale-95"
      aria-label={t("activity.title")}
      data-testid="home-notification-bell"
    >
      <Bell className="h-[18px] w-[18px] text-[#0D47A1]" strokeWidth={2.2} />
      {n > 0 && (
        <span className="absolute -right-1 -top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[#E53935] px-1 text-[10px] font-extrabold leading-none text-white ring-2 ring-white" data-testid="home-notification-count">
          {n > 9 ? "9+" : n}
        </span>
      )}
    </button>
  );
}

function CategoryCard({ cat, index, onClick }) {
  const { lang } = useLang();
  return (
    <button
      onClick={onClick}
      className="group flex flex-col rounded-2xl border-[1.5px] border-navy bg-white p-1.5 text-left transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_20px_rgba(13,42,107,0.15)] active:scale-[0.97] fade-up"
      style={{ animationDelay: `${index * 30}ms` }}
      data-testid={`category-card-${cat.key}`}
    >
      <div className="relative aspect-[4/3] w-full overflow-hidden rounded-xl bg-mist">
        <img src={cat.image} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        {cat.adult && (
          <span className="absolute right-1.5 top-1.5 rounded-full bg-alert px-2 py-0.5 text-[10px] font-extrabold text-white shadow" data-testid="category-card-adult-badge">+18</span>
        )}
      </div>
      <span className="block truncate px-1 pb-0.5 pt-2 text-center text-[16px] font-bold leading-tight text-navy sm:text-[17px]">
        <BrandText text={lang === "en" ? cat.name_en : cat.name_es} />
      </span>
    </button>
  );
}

export default function HomeScreen() {
  const { t } = useLang();
  const navigate = useNavigate();
  const { categories, guardCategory } = useAppData();
  const { region } = useRegion();
  const banners = useBanners(region.slug);
  const [scrolled, setScrolled] = useState(false);

  return (
    <div className="flex h-full flex-col overflow-hidden bg-white" data-testid="home-screen">
      <div className={`relative shrink-0 ${banners?.length ? "h-[40dvh]" : "h-[calc(env(safe-area-inset-top)+3.75rem)] bg-white"}`} data-testid="home-hero">
        <h1 className="sr-only">{region.brand_title}</h1>
        {banners?.length > 0 && <BannerCarousel banners={banners} />}
        <NotificationBell />
        <LangTextToggle className="absolute right-3 top-safe-3 z-20" />
      </div>
      <div className="relative z-10 flex min-h-0 flex-1 flex-col bg-white">
        <div className="relative min-h-0 flex-1">
          <div
            className={`pointer-events-none absolute inset-x-0 top-0 z-10 h-4 bg-gradient-to-b from-navy/10 to-transparent transition-opacity duration-200 ${scrolled ? "opacity-100" : "opacity-0"}`}
            data-testid="home-grid-shadow"
          />
          <div
            className="h-full overflow-y-auto overscroll-contain px-[15px] pb-[calc(env(safe-area-inset-bottom)+6.5rem)] pt-3 sm:px-6"
            onScroll={(e) => setScrolled(e.currentTarget.scrollTop > 2)}
            data-testid="home-category-scroll"
          >
            <div className="mx-auto grid max-w-5xl grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-3.5 lg:grid-cols-4" data-testid="home-category-grid">
              {categories.map((c, i) => (
                <CategoryCard key={c.key} cat={c} index={i} onClick={() => guardCategory(c.key, () => navigate(`/mercat?category=${c.key}`))} />
              ))}
            </div>
            <p className="mt-6 text-center text-xs text-slate-400">{t("home.footer")}</p>
            <LegalLinks className="mt-2" testid="home-legal-links" />
          </div>
        </div>
      </div>
    </div>
  );
}
